// Typed API layer for the CRM phone-call endpoints consumed by the admin SPA
// (Fase 5 — global Calls tab). Thin wrappers over crmRequest so views never
// inline paths and share one place for the phone-call contract + its labels.

import { CrmHttpError, crmRequest } from "./crm";
import { canonicalLeadIdOrNull } from "./crmLeadId";
import {
  CrmAdvisor,
  PhoneCallDirectionFilter,
  PhoneCallStatus,
  PhoneCallsListFilters,
  PhoneCallsListResponse,
} from "./crmTypes";

// Human-readable direction labels (es-ES). Domain copy lives here, not in the
// view. `direction` on a row is free string ('unknown' for migrated history);
// unknown values fall back to the raw string via the ?? in the caller.
export const DIRECTION_LABELS: Record<PhoneCallDirectionFilter, string> = {
  inbound: "Entrante",
  outbound: "Saliente",
};

// The subset of directions offered as a filter (closed union), with labels for
// the dropdown. Derived from DIRECTION_LABELS so both stay in lockstep (one
// source of truth for the labels).
export const DIRECTION_FILTER_OPTIONS: {
  value: PhoneCallDirectionFilter;
  label: string;
}[] = (Object.keys(DIRECTION_LABELS) as PhoneCallDirectionFilter[]).map(
  (value) => ({ value, label: DIRECTION_LABELS[value] }),
);

// Human-readable status labels (es-ES) for the final orchestrator statuses.
export const STATUS_LABELS: Record<PhoneCallStatus, string> = {
  queued: "En cola",
  ringing: "Sonando",
  in_progress: "En curso",
  completed: "Completada",
  no_answer: "Sin respuesta",
  busy: "Ocupado",
  failed: "Fallida",
  voicemail: "Buzón",
  canceled: "Cancelada",
};

// The statuses worth offering as a filter. The list excludes in_progress (the
// view is a log of TERMINATED calls — the CRM repo filters those out) and the
// transient queued/ringing, matching what a completed-call log surfaces.
export const STATUS_FILTER_OPTIONS: { value: PhoneCallStatus; label: string }[] =
  [
    { value: "completed", label: "Completada" },
    { value: "no_answer", label: "Sin respuesta" },
    { value: "busy", label: "Ocupado" },
    { value: "failed", label: "Fallida" },
    { value: "voicemail", label: "Buzón" },
    { value: "canceled", label: "Cancelada" },
  ];

// Pill colour scheme per status: green for completed, muted for the rest.
export function statusScheme(
  status: string,
): "muted" | "green" | "amber" | "blue" {
  if (status === "completed") return "green";
  if (status === "no_answer" || status === "busy") return "amber";
  if (status === "failed" || status === "canceled") return "muted";
  return "blue";
}

// Pill colour scheme per direction: green for inbound, blue for the rest
// (outbound / 'unknown' migrated history). Lives here next to statusScheme so
// both pill colour maps share one home.
export function dirScheme(direction: string): "green" | "blue" {
  return direction === "inbound" ? "green" : "blue";
}

// mm:ss from a duration in seconds. Guards against null, negatives and
// non-finite input (NaN/Infinity from bad data) -> "—". Math.floor on both the
// minutes and the seconds so a fractional duration never prints "3:7.5".
export function fmtDuration(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Turn a YYYY-MM-DD date-picker value into a full ISO-8601 UTC instant for the
// EDGE of the day, interpreted in the OPERATOR'S LOCAL timezone.
//
// The `<input type="date">` yields a bare calendar day with no time or zone.
// The CRM's dateFrom/dateTo are inclusive datetime bounds compared against
// `occurred_at` (a UTC instant). If we forwarded the bare "2026-07-14" the
// backend would read it as 00:00:00 UTC, so the whole last day (dateTo) fell
// OUTSIDE the range — the confirmed "dateTo excludes the day" bug — and, in
// timezones behind UTC, part of the first day too.
//
// Fix: expand each edge to the day's boundary in LOCAL time and serialize to
// UTC. `new Date("2026-07-14T23:59:59.999")` (no zone suffix) is parsed as
// LOCAL time by JS, and `.toISOString()` converts it to the equivalent UTC
// instant — so a Madrid operator's "14 Jul" ends at 21:59:59.999Z (summer),
// exactly the local end of their day. `edge` picks start vs end of day.
function dayBoundaryIso(day: string, edge: "start" | "end"): string {
  const time = edge === "start" ? "T00:00:00.000" : "T23:59:59.999";
  return new Date(`${day}${time}`).toISOString();
}

// GET /phone/calls — shared team call log (1 row per real call). Keyset-cursor
// paginated: pass `cursor` (the previous page's nextCursor) for O(index)
// navigation. The endpoint is KEYSET-PURE — there is no page/offset param; the
// only navigation is `cursor` + `sortDir`. All filters optional and combinable.
// The query string is built with URLSearchParams (never concatenation). Visible
// to any internal role (sales|ops|admin); never 404s (empty items if no match).
export function listPhoneCalls(
  filters: PhoneCallsListFilters,
): Promise<PhoneCallsListResponse> {
  const params = new URLSearchParams();
  // dateFrom/dateTo arrive as YYYY-MM-DD; expand to local-day boundaries in UTC
  // so the range respects the operator's timezone (see dayBoundaryIso).
  if (filters.dateFrom)
    params.set("dateFrom", dayBoundaryIso(filters.dateFrom, "start"));
  if (filters.dateTo)
    params.set("dateTo", dayBoundaryIso(filters.dateTo, "end"));
  if (filters.agentEmail) params.set("agentEmail", filters.agentEmail);
  if (filters.direction) params.set("direction", filters.direction);
  if (filters.status) params.set("status", filters.status);
  if (filters.market) params.set("market", filters.market);
  if (filters.phone) params.set("phone", filters.phone);
  if (filters.pageSize !== undefined)
    params.set("pageSize", String(filters.pageSize));
  if (filters.sortDir) params.set("sortDir", filters.sortDir);
  if (filters.cursor) params.set("cursor", filters.cursor);
  const qs = params.toString();
  return crmRequest<PhoneCallsListResponse>(
    `/phone/calls${qs ? `?${qs}` : ""}`,
  ).then((res) => ({
    ...res,
    // El CRM devuelve `leadId` en hex-32 interno; el admin-api de Operations
    // solo acepta el ULID canónico → normalizamos aquí para que la vista pueda
    // linkar a la ficha sin dar 400 (null si la llamada no resolvió lead).
    items: res.items.map((row) => ({
      ...row,
      leadId: canonicalLeadIdOrNull(row.leadId),
    })),
  }));
}

// GET /advisors -> { items } — advisors for the agent dropdown. The CRM now
// allows 'ops' on this endpoint (aligned with phone.plugin), but OLDER CRM
// deploys still gate it to admin|sales and answer 403 to an ops user. We keep
// the fail-soft: a 403 yields an empty list so the tab still works (the agent
// filter falls back to a free-text email input) instead of erroring. Any OTHER
// error propagates so the caller can surface a small notice (and a 401 still
// reaches the CrmConnectGate to prompt reconnect).
export function listAdvisors(): Promise<CrmAdvisor[]> {
  return crmRequest<{ items: CrmAdvisor[] }>("/advisors")
    .then((res) => res.items)
    .catch((err) => {
      if (err instanceof CrmHttpError && err.status === 403) return [];
      throw err;
    });
}
